import {
    IconAlertTriangle,
    IconBolt,
    IconBrain,
    IconClipboard,
    IconPencil,
    IconRobot,
    IconSettings,
    IconTarget
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import QuickTrainMerged from '../components/QuickTrainMerged';
import { ConfirmationDialog } from '../components/ui/confirmation-dialog';
import { Toaster } from '../components/ui/toaster';
import { GHL } from '../ghl';
import { useToast } from '../hooks/use-toast';
import { debug } from '../utils/debug';

// Default AI instructions for spam detection (FALLBACK ONLY)
// The actual default is loaded from /data/prompts/AI_SPAM_DETECTION_PROMPT.md
// This is only used if the API fails to load the prompt
const DEFAULT_AI_INSTRUCTIONS = `You are an expert email spam detector for business emails.

Analyze the email and determine if it's spam. Consider:

**Common spam types:**
- Phishing attempts (fake password resets, account verifications)
- Catfishing/dating scams (profile links, "get to know you")
- Fake document signing requests (not from legitimate services)
- Prize/lottery scams
- Cryptocurrency/investment scams
- Unsolicited sales pitches with no prior relationship
- Suspicious tracking links or shortened URLs

**Legitimate emails typically:**
- Reference specific business context
- Come from known service providers
- Use proper grammar and professional tone
- Have relevant, personalized content
- Inquiries about your services/products
- Inquiries from known contacts or domains
- Inquiries referencing previous interactions
- Inquiries from customers or clients
- Don't pressure immediate action with threats
- Anything from miltonecom.com, gohighlevel.com, hamiltonecom.com, torontoecom.com, or calgaryecom.com

**Response format:**
Return ONLY valid JSON with this structure:
{
    "is_spam": true/false,
    "confidence": 0.0-1.0,
    "reasoning": "Brief explanation of your decision",
    "spam_categories": ["category1", "category2"],
    "suggested_action": "tag_spam" or "review_manual" or "allow"
}`;

export default function Settings() {
  const [searchParams] = useSearchParams();
  const urlLocationId = searchParams.get('location_id');
  const { toast } = useToast();

  // Detect GHL template variable (not interpolated) and treat as empty
  const isTemplateVariable = urlLocationId === '{{location.id}}' || urlLocationId?.includes('{{');
  const validUrlLocationId = isTemplateVariable ? null : urlLocationId;

  // Get location_id from URL or localStorage - persists across sessions
  const [locationId, setLocationId] = useState(() => {
    const fromUrl = validUrlLocationId;
    const fromStorage = localStorage.getItem('ghl_location_id');

    // Also validate localStorage value isn't a template variable
    const isStorageTemplateVariable = fromStorage === '{{location.id}}' || fromStorage?.includes('{{');
    const validFromStorage = isStorageTemplateVariable ? null : fromStorage;

    return fromUrl || validFromStorage;
  });

  const [locationResolved, setLocationResolved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [alert, setAlert] = useState(null);
  const [activeTab, setActiveTab] = useState('general'); // 'general' or 'ai-prompt'
  const [aiInstructions, setAiInstructions] = useState(DEFAULT_AI_INSTRUCTIONS);
  const [aiInstructionsModified, setAiInstructionsModified] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [settings, setSettings] = useState({
    spamThreshold: 0.7,
    autoTag: true,
    autoDnd: true,
    scanFrequency: 3600,
    notifyOnSpam: false,
    notifyEmail: '',
  });

  useEffect(() => {
    // Store location_id in localStorage when it's available in URL
    if (validUrlLocationId) {
      localStorage.setItem('ghl_location_id', validUrlLocationId);
      setLocationId(validUrlLocationId);
    }
  }, [validUrlLocationId]);

  // Load AI instructions from backend
  // Load AI instructions from API
  useEffect(() => {
    const loadAIInstructions = async () => {
      if (!locationId) return;

      try {
        const response = await fetch(`/api/locations/${locationId}/ai-instructions`);
        if (response.ok) {
          const data = await response.json();
          if (data.ai_instructions) {
            setAiInstructions(data.ai_instructions);
          }
        }
      } catch (err) {
        debug.error('[Settings] Failed to load AI instructions:', err);
        // Fallback to default if API fails
        setAiInstructions(DEFAULT_AI_INSTRUCTIONS);
      }
    };

    loadAIInstructions();
  }, [locationId]);

  // Resolve location_id via SSO if needed
  useEffect(() => {
    const resolveLocation = async () => {
      if (locationId) {
        setLocationResolved(true);
        return;
      }

      // Try SSO
      try {
        const ghl = new GHL();
        const userData = await ghl.getUserData();
        const locationFromSSO = userData?.activeLocation || userData?.locationId;

        if (locationFromSSO) {
          localStorage.setItem('ghl_location_id', locationFromSSO);
          setLocationId(locationFromSSO);
        }
      } catch (err) {
        debug.error('[Settings] SSO failed:', err);
      } finally {
        setLocationResolved(true);
      }
    };

    if (!locationResolved) {
      resolveLocation();
    }
  }, []);

  const showAlert = (message, type = 'error') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleAIInstructionsChange = (value) => {
    setAiInstructions(value);
    setAiInstructionsModified(true);
    setSaved(false);
  };

  const validateAIInstructions = (instructions) => {
    const MIN_LENGTH = 50;
    const MAX_LENGTH = 50000;

    // Length validation
    if (instructions.length < MIN_LENGTH || instructions.length > MAX_LENGTH) {
      return { valid: false };
    }

    // Check for required response format keywords
    const requiredKeywords = ['is_spam', 'confidence', 'reasoning'];
    const missingKeywords = requiredKeywords.filter(keyword => !instructions.includes(keyword));

    if (missingKeywords.length > 0) {
      return { valid: false };
    }

    // Sanitize input - detect dangerous patterns
    const dangerousPatterns = [
      /<script[^>]*>/i,
      /javascript:/i,
      /on\w+\s*=/i,
      /;\s*DROP\s+/i,
      /;\s*DELETE\s+FROM\s+/i,
      /EXEC\s*\(/i
    ];

    for (const pattern of dangerousPatterns) {
      if (pattern.test(instructions)) {
        return { valid: false };
      }
    }

    return { valid: true };
  };

  const handleResetAIInstructions = () => {
    setAiInstructions(DEFAULT_AI_INSTRUCTIONS);
    setAiInstructionsModified(true);
    setSaved(false);
    setResetDialogOpen(false);
  };

  const handleSaveAIInstructions = async () => {
    // Validate before sending to backend
    const validation = validateAIInstructions(aiInstructions);
    if (!validation.valid) {
      toast({
        variant: "destructive",
        title: "Invalid AI prompt",
        description: "Please check your AI instructions and try again."
      });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/locations/${locationId}/ai-instructions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ai_instructions: aiInstructions })
      });

      if (!response.ok) {
        // Don't expose backend error details
        throw new Error('Failed to save');
      }

      setSaved(true);
      setAiInstructionsModified(false);
      toast({
        variant: "success",
        title: "Success",
        description: "AI instructions saved successfully!"
      });
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save AI instructions. Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  if (!locationId) {
    return (
      <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg p-6">
        <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
          <IconAlertTriangle size={28} className="text-yellow-400" />
          No Location
        </h2>
        <p>Please access this page from within the platform to configure settings for your location.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Styled Alert */}
      {alert && (
        <div className={`rounded-lg p-4 border transition-all ${
          alert.type === 'error'
            ? 'bg-red-500/20 border-red-500 text-red-200'
            : alert.type === 'warning'
            ? 'bg-yellow-500/20 border-yellow-500 text-yellow-200'
            : 'bg-green-500/20 border-green-500 text-green-200'
        }`}>
          <p className="font-medium">{alert.message}</p>
        </div>
      )}

      <div>
        <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
          <IconSettings size={40} />
          Settings
        </h1>
        <p className="text-lg opacity-80">Configure spam detection for your location</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-white/20">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('general')}
            className={`px-6 py-3 font-semibold transition-all border-b-2 ${
              activeTab === 'general'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-white/60 hover:text-white/80'
            }`}
          >
            <span className="flex items-center gap-2">
              <IconSettings size={20} />
              General Settings
            </span>
          </button>
          <button
            onClick={() => setActiveTab('ai-prompt')}
            className={`px-6 py-3 font-semibold transition-all border-b-2 ${
              activeTab === 'ai-prompt'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-white/60 hover:text-white/80'
            }`}
          >
            <span className="flex items-center gap-2">
              <IconRobot size={20} />
              AI Prompt Editor
            </span>
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'general' ? (
        <div className="space-y-8">
      {/* Spam Detection Settings */}
      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <IconTarget size={28} />
          Spam Detection
        </h2>

        <div className="space-y-6">
          <div>
            <label className="block mb-2 font-medium">
              Spam Confidence Threshold
            </label>
            <p className="text-sm opacity-70 mb-2">
              Emails with confidence above this threshold will be marked as spam (0-100%)
            </p>
            <input
              type="range"
              min="0"
              max="100"
              value={settings.spamThreshold * 100}
              onChange={(e) => handleChange('spamThreshold', e.target.value / 100)}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-sm mt-1">
              <span>Low (0%)</span>
              <span className="font-bold">{(settings.spamThreshold * 100).toFixed(0)}%</span>
              <span>High (100%)</span>
            </div>
          </div>

          <div>
            <label className="block mb-2 font-medium">
              Scan Frequency
            </label>
            <p className="text-sm opacity-70 mb-2">
              How often to check for new spam emails
            </p>
            <select
              value={settings.scanFrequency}
              onChange={(e) => handleChange('scanFrequency', parseInt(e.target.value))}
              className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-white/20"
            >
              <option value="1800">Every 30 minutes</option>
              <option value="3600">Every hour</option>
              <option value="7200">Every 2 hours</option>
              <option value="14400">Every 4 hours</option>
              <option value="86400">Once daily</option>
            </select>
          </div>
        </div>
      </div>

      {/* Advanced Rules Link */}
      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
        <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
          <IconBrain size={28} />
          Advanced Spam Rules
        </h2>
        <p className="text-sm opacity-80 mb-4">
          Customize spam indicators without code. Add your own keywords and business context so the AI adapts to your inbox.
        </p>
        <a
          href={`/spam-settings.html?location_id=${encodeURIComponent(locationId)}`}
          target="_blank"
          rel="noreferrer"
          className="inline-block px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-semibold"
        >
          Open Advanced Spam Rules
        </a>
      </div>

      {/* Quick Train Merged with Time Machine History */}
      <div>
        <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
          <IconPencil size={32} />
          Train Your AI
        </h2>
        <QuickTrainMerged locationId={locationId} />
      </div>

      {/* Auto-Actions */}
      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <IconBolt size={28} />
          Automatic Actions
        </h2>

        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <input
              type="checkbox"
              id="autoTag"
              checked={settings.autoTag}
              onChange={(e) => handleChange('autoTag', e.target.checked)}
              className="mt-1 w-5 h-5 rounded"
            />
            <div className="flex-1">
              <label htmlFor="autoTag" className="font-medium cursor-pointer">
                Auto-tag spam contacts
              </label>
              <p className="text-sm opacity-70">
                Automatically add "spam", "fishing", and "security" tags to detected spam contacts
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <input
              type="checkbox"
              id="autoDnd"
              checked={settings.autoDnd}
              onChange={(e) => handleChange('autoDnd', e.target.checked)}
              className="mt-1 w-5 h-5 rounded"
            />
            <div className="flex-1">
              <label htmlFor="autoDnd" className="font-medium cursor-pointer">
                Enable DND ALL on spam contacts
              </label>
              <p className="text-sm opacity-70">
                Automatically block spam contacts from all communication channels (Call, Email, SMS, WhatsApp, GMB, FB)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
        <h2 className="text-2xl font-bold mb-4">🔔 Notifications</h2>

        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <input
              type="checkbox"
              id="notifyOnSpam"
              checked={settings.notifyOnSpam}
              onChange={(e) => handleChange('notifyOnSpam', e.target.checked)}
              className="mt-1 w-5 h-5 rounded"
            />
            <div className="flex-1">
              <label htmlFor="notifyOnSpam" className="font-medium cursor-pointer">
                Email notifications for spam
              </label>
              <p className="text-sm opacity-70">
                Receive email notifications when spam is detected
              </p>
            </div>
          </div>

          {settings.notifyOnSpam && (
            <div>
              <label className="block mb-2 font-medium">
                Notification Email
              </label>
              <input
                type="email"
                value={settings.notifyEmail}
                onChange={(e) => handleChange('notifyEmail', e.target.value)}
                placeholder="your@email.com"
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-white/20"
              />
            </div>
          )}
        </div>
      </div>

      {/* Custom Rules (Future) */}
      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20 opacity-50">
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <IconClipboard size={28} />
          Custom Rules
        </h2>
        <p className="text-sm opacity-70">
          Custom spam detection rules are coming soon. This will allow you to define specific patterns, keywords, or sender domains to always flag or allow.
        </p>
      </div>

      {/* Save Button */}
      <div className="flex gap-4 items-center">
        <button
          onClick={handleSaveAIInstructions}
          disabled={loading || saved}
          className={`px-8 py-3 rounded-lg font-bold transition-all ${
            saved
              ? 'bg-green-500 text-white'
              : loading
              ? 'bg-gray-600 text-white cursor-not-allowed'
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          }`}
        >
          {saved ? '✅ Saved!' : loading ? '⏳ Saving...' : '💾 Save Settings'}
        </button>

        {saved && (
          <p className="text-green-400 font-medium">
            Settings saved successfully!
          </p>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/20 border border-blue-500 rounded-lg p-6">
        <h3 className="font-bold mb-2">💡 Need Help?</h3>
        <p className="text-sm opacity-90">
          Visit our <a href="/docs" className="underline">Documentation</a> or <a href="/support" className="underline">Contact Support</a> if you need assistance configuring your spam detection settings.
        </p>
      </div>
        </div>
      ) : (
        /* AI Prompt Editor Tab */
        <div className="space-y-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-2 mb-2">
                  <IconRobot size={28} />
                  AI Spam Detection Prompt
                </h2>
                <p className="text-sm opacity-80">
                  Customize how the AI identifies spam emails. Edit the instructions below to adapt the detector to your specific business needs.
                </p>
              </div>
              <button
                onClick={() => setResetDialogOpen(true)}
                className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white font-medium transition-all"
              >
                Reset to Default
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block mb-2 font-medium text-lg">
                  AI Instructions
                </label>
                <textarea
                  value={aiInstructions}
                  onChange={(e) => handleAIInstructionsChange(e.target.value)}
                  className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-white/20 font-mono text-sm"
                  rows={25}
                  placeholder="Enter your custom AI instructions..."
                />
                <div className="mt-2 space-y-2">
                  <p className="text-xs opacity-60">
                    The AI will use these instructions to analyze emails and determine if they're spam.
                    Be specific about your business context and what types of emails are legitimate for your industry.
                  </p>
                  <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-3">
                    <p className="text-sm font-semibold mb-1 flex items-center gap-2">
                      <IconRobot size={16} />
                      Auto-filled Placeholders
                    </p>
                    <p className="text-xs opacity-80">
                      Placeholders like <code className="bg-black/30 px-1.5 py-0.5 rounded">{'{BUSINESS_DOMAINS}'}</code>, <code className="bg-black/30 px-1.5 py-0.5 rounded">{'{BUSINESS_NAME}'}</code>, and <code className="bg-black/30 px-1.5 py-0.5 rounded">{'{BUSINESS_EMAILS}'}</code> are automatically replaced with your actual business information during spam detection. You don't need to edit them manually.
                    </p>
                  </div>
                </div>
              </div>

              {aiInstructionsModified && (
                <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg p-4">
                  <p className="text-yellow-200 text-sm">
                    ⚠️ You have unsaved changes. Click "Save AI Instructions" below to apply them.
                  </p>
                </div>
              )}

              <div className="flex gap-4 items-center">
                <button
                  onClick={handleSaveAIInstructions}
                  disabled={loading || (saved && !aiInstructionsModified)}
                  className={`px-8 py-3 rounded-lg font-bold transition-all ${
                    saved && !aiInstructionsModified
                      ? 'bg-green-500 text-white'
                      : loading
                      ? 'bg-gray-600 text-white cursor-not-allowed'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                >
                  {saved && !aiInstructionsModified ? '✅ Saved!' : loading ? '⏳ Saving...' : '💾 Save AI Instructions'}
                </button>

                {saved && !aiInstructionsModified && (
                  <p className="text-green-400 font-medium">
                    AI instructions saved successfully!
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="bg-blue-500/20 border border-blue-500 rounded-lg p-6">
            <h3 className="font-bold mb-3 text-lg">💡 Tips for Writing Effective AI Instructions</h3>
            <ul className="space-y-2 text-sm opacity-90">
              <li>• Be specific about your industry and business type</li>
              <li>• List common legitimate email patterns you receive</li>
              <li>• Mention known vendor domains and service providers</li>
              <li>• Describe typical spam you want to catch (phishing, catfishing, scams)</li>
              <li>• Keep the JSON response format at the end unchanged</li>
              <li>• Test changes on a few emails before applying to all conversations</li>
            </ul>
          </div>
        </div>
      )}

      {/* Reset Confirmation Dialog */}
      <ConfirmationDialog
        open={resetDialogOpen}
        onOpenChange={setResetDialogOpen}
        onConfirm={handleResetAIInstructions}
        title="Reset AI Instructions"
        description="Reset AI instructions to default? This will lose any custom changes."
        confirmText="Reset"
        cancelText="Cancel"
        variant="danger"
      />
      <Toaster />
    </div>
  );
}

